import { newId } from "./ids";
import { nowIso } from "./format";
import { DEFAULT_LAYOUT, type Action, type ActionType, type Category, type CategoryLayout, type Id, type Library, type OpenWith, type Workspace } from "./types";

const byPos = <T extends { position: number }>(a: T, b: T) => a.position - b.position;
const nextPos = (list: { position: number }[]) => list.reduce((m, x) => Math.max(m, x.position), -1) + 1;

/* ---------- selectors ---------- */
export const workspacesOf = (lib: Library): Workspace[] => [...lib.workspaces].sort(byPos);
export const categoriesOf = (lib: Library, workspaceId: Id): Category[] =>
  lib.categories.filter(c => c.workspaceId === workspaceId).sort(byPos);
export const actionsOf = (lib: Library, categoryId: Id): Action[] =>
  lib.actions.filter(a => a.categoryId === categoryId).sort(byPos);
export const activeWorkspace = (lib: Library): Workspace | undefined =>
  lib.workspaces.find(w => w.id === lib.settings.activeWorkspaceId) ?? workspacesOf(lib)[0];
export const getWorkspace = (lib: Library, id: Id) => lib.workspaces.find(w => w.id === id);
export const getCategory = (lib: Library, id: Id) => lib.categories.find(c => c.id === id);
export const getAction = (lib: Library, id: Id) => lib.actions.find(a => a.id === id);
export const totals = (lib: Library) => ({ workspaces: lib.workspaces.length, categories: lib.categories.length, actions: lib.actions.length });
export const hasSample = (lib: Library) =>
  lib.workspaces.some(x => x.sample) || lib.categories.some(x => x.sample) || lib.actions.some(x => x.sample);

/* ---------- mutations (in place; the store persists after each one) ---------- */
export interface WorkspaceInput { name: string; icon: string }
/** layout left out: "standard" for a new category, unchanged for an existing one */
export interface CategoryInput { name: string; icon: string; glow: string; layout?: CategoryLayout }
export interface ActionInput { type: ActionType; name: string; icon: string; color: string; url: string; text: string }
/**
 * Id and creation time for a new record. An editor creates them once when it opens, so pressing save again after a
 * failed or lost save re-sends the same record instead of adding a second one.
 */
export interface NewMeta { id?: Id; at?: string }

export function addWorkspace(lib: Library, input: WorkspaceInput, meta: NewMeta = {}): Workspace {
  const t = meta.at ?? nowIso();
  const ws: Workspace = { id: meta.id ?? newId("workspace"), name: input.name, icon: input.icon, position: nextPos(lib.workspaces), createdAt: t, updatedAt: t };
  lib.workspaces.push(ws);
  lib.settings.activeWorkspaceId = ws.id;
  return ws;
}
export function updateWorkspace(lib: Library, id: Id, input: WorkspaceInput) {
  const ws = getWorkspace(lib, id); if (!ws) return;
  Object.assign(ws, { name: input.name, icon: input.icon, updatedAt: nowIso() });
}
/** Deletes the workspace and everything inside it. The last workspace cannot be deleted. */
export function deleteWorkspace(lib: Library, id: Id): boolean {
  if (lib.workspaces.length <= 1) return false;
  const catIds = new Set(lib.categories.filter(c => c.workspaceId === id).map(c => c.id));
  lib.actions = lib.actions.filter(a => !catIds.has(a.categoryId));
  lib.categories = lib.categories.filter(c => c.workspaceId !== id);
  lib.workspaces = lib.workspaces.filter(w => w.id !== id);
  if (lib.settings.activeWorkspaceId === id) lib.settings.activeWorkspaceId = workspacesOf(lib)[0]?.id ?? null;
  return true;
}

export function addCategory(lib: Library, workspaceId: Id, input: CategoryInput, meta: NewMeta = {}): Category {
  const t = meta.at ?? nowIso();
  const c: Category = { id: meta.id ?? newId("category"), workspaceId, name: input.name, icon: input.icon, glow: input.glow,
    layout: input.layout ?? DEFAULT_LAYOUT, position: nextPos(categoriesOf(lib, workspaceId)), createdAt: t, updatedAt: t };
  lib.categories.push(c);
  return c;
}
export function updateCategory(lib: Library, id: Id, input: CategoryInput) {
  const c = getCategory(lib, id); if (!c) return;
  Object.assign(c, { name: input.name, icon: input.icon, glow: input.glow }, input.layout ? { layout: input.layout } : {}, { updatedAt: nowIso() });
}
export function deleteCategory(lib: Library, id: Id) {
  lib.actions = lib.actions.filter(a => a.categoryId !== id);
  lib.categories = lib.categories.filter(c => c.id !== id);
}

const cleanAction = (input: ActionInput): ActionInput => ({
  ...input, url: input.type === "link" ? input.url : "", text: input.type === "memo" ? input.text : ""
});
export function addAction(lib: Library, categoryId: Id, input: ActionInput, meta: NewMeta = {}): Action {
  const t = meta.at ?? nowIso();
  const a: Action = { id: meta.id ?? newId("action"), categoryId, ...cleanAction(input), position: nextPos(actionsOf(lib, categoryId)), createdAt: t, updatedAt: t };
  lib.actions.push(a);
  touch(getCategory(lib, categoryId), nowIso());
  return a;
}
export function updateAction(lib: Library, id: Id, input: ActionInput) {
  const a = getAction(lib, id); if (!a) return;
  const t = nowIso();
  Object.assign(a, cleanAction(input), { updatedAt: t });
  touch(getCategory(lib, a.categoryId), t);
}
export function setActionUrl(lib: Library, id: Id, url: string) {
  const a = getAction(lib, id); if (!a) return;
  a.url = url; a.updatedAt = nowIso();
}
export function deleteAction(lib: Library, id: Id) {
  const a = getAction(lib, id); if (!a) return;
  lib.actions = lib.actions.filter(x => x.id !== id);
  touch(getCategory(lib, a.categoryId), nowIso());
}
function touch(c: Category | undefined, t: string) { if (c) c.updatedAt = t; }

export function setActiveWorkspace(lib: Library, id: Id) { if (getWorkspace(lib, id)) lib.settings.activeWorkspaceId = id; }
export function setOpenWith(lib: Library, v: OpenWith) { lib.settings.openWith = v; }

export const cloneLibrary = (lib: Library): Library => JSON.parse(JSON.stringify(lib));
